<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <h1 class="sci-page-title-only">Relatório TCE: Máquinas</h1>
        <p class="text-sm text-slate-500 font-medium mt-1">Consulta e exportação de dados de máquinas para o Tribunal de Contas</p>
      </div>

      <!-- ÁREA DE FILTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-filter-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  FILTRAR CONSULTA
              </h2>
          </div>
          <div class="p-8">
              <v-row dense>
                <v-col cols="12" sm="3" md="3">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Mês e Ano</label>
                  <DatePicker
                    @dateValue="[(filter.date = $event)]"
                    type="month"
                  />
                </v-col>
                <v-col cols="12" sm="9" md="9">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
                  <v-autocomplete
                    :items="prefectures"
                    v-model="filter.prefecture_id"
                    item-value="prefecture_id"
                    item-text="prefecture_social_name"
                    placeholder="Selecione a prefeitura"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                    @change="getCode(filter.prefecture_id)"
                  />
                </v-col>
                <v-col cols="12">
                   <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Código da Unidade Gestora</label>
                  <v-autocomplete
                    :items="codigos"
                    v-model="filter.cod"
                    item-value="cod"
                    item-text="cod"
                    placeholder="Selecione o código"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                  />
                </v-col>
              </v-row>

               <div class="flex justify-end gap-3 mt-8">
                  <v-btn
                    :to="`/report/tce/maquinas/${filter.date?.substring(5)}/${filter.date?.substring(0, 4)}/${filter.prefecture_id}/${filter.cod}`"
                    depressed
                    :disabled="!filter.prefecture_id || !filter.cod || !filter.date"
                    class="bg-slate-100 text-slate-500 font-bold px-6 h-10 rounded-lg transition-all text-[11px] uppercase tracking-widest shadow-none hover:bg-slate-200"
                  >
                    Gerar Relatório
                  </v-btn>
                  <button 
                    @click="search"
                    :disabled="isButtonDisabled || loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/20 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-10 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-bold uppercase tracking-widest"
                  >
                      <v-icon color="white" small>mdi-filter-variant</v-icon>
                      {{ loader ? 'FILTRANDO...' : 'FILTRAR' }}
                  </button>
              </div>
          </div>
      </section>

      <!-- RESULTADO -->
      <section v-if="data.length > 0 || loader" class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                  <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                      RESULTADOS ENCONTRADOS
                  </h2>
              </div>
              <button 
                @click="exportFile"
                :disabled="isButtonDisabled || data.length === 0"
                class="bg-white/10 hover:bg-white/20 text-white font-bold text-[10px] px-6 py-2 rounded-lg border border-white/20 transition-all uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
              >
                <v-icon small color="white">mdi-download</v-icon>
                Exportar TXT
              </button>
          </div>
          
          <v-data-table 
            :loading="loader" 
            :items="data" 
            :headers="headers"
            :items-per-page="15"
            loading-text="CARREGANDO DADOS..."
            class="elevation-0"
          >
            <template v-slot:item.tipoMaquina="{ item }">
              <span class="text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-wider" :class="{
                  'bg-blue-50 text-blue-600': item.tipoMaquina == 1,
                  'bg-purple-50 text-purple-600': item.tipoMaquina == 2,
                  'bg-amber-50 text-amber-600': item.tipoMaquina == 3,
                  'bg-slate-50 text-slate-600': item.tipoMaquina == 4,
              }">
                {{
                  item.tipoMaquina == 1
                    ? "Próprio"
                    : item.tipoMaquina == 2
                    ? "Locado"
                    : item.tipoMaquina == 3
                    ? "Prestação de Serviço"
                    : "Cedido"
                }}
              </span>
            </template>
            <template v-slot:item.descricao="{ item }">
                <span class="text-xs font-semibold text-slate-700">{{ item.descricao }}</span>
            </template>
            <template v-slot:item.codigo="{ item }">
                <span class="text-xs font-mono font-bold text-slate-500">{{ item.codigo }}</span>
            </template>
          </v-data-table>
      </section>

      <v-snackbar
        multi-line
        :color="snack.color"
        v-model="snack.show"
        transition="fade-transition"
        top right
      >
        <span class="text-[11px] font-bold uppercase tracking-widest">{{ snack.message }}</span>
        <template v-slot:action="{ attrs }">
          <v-btn dark text v-bind="attrs" @click="snack.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import DatePicker from "@/components/InputDate.vue";
export default {
  components: { DatePicker },
  mixins: [Helpers],
  data() {
    return {
      loader: false,
      data: [],
      prefectures: [],
      codigos: [],
      filter: {
        prefecture_id: null,
        date: null,
        cod: null,
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "Unidade Gestora", value: "codUnidadeGestora" },
        { text: "Código Próprio", value: "codigo" },
        { text: "Ano Fab.", value: "anoFabricacao", align: "center" },
        { text: "Descrição da Máquina", value: "descricao" },
        { text: "Tipo", value: "tipoMaquina", align: "center" },
        { text: "CPF/CNPJ Proprietário", value: "cpfcnpjProprietario" },
        { text: "CPF/CNPJ Locador", value: "cpfcnpjLocador" },
      ],
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    isButtonDisabled() {
      return !(
        this.filter.prefecture_id &&
        this.filter.date &&
        this.filter.cod
      );
    },
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http.get("/prefectures")
        .then((r) => {
            this.prefectures = r.data;
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
        })
        .catch(() => {
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
        });
    },
    getCode(prefecture_id) {
      this.$http(`/tce/codigo/${prefecture_id}`).then(
        (r) => (this.codigos = r.data)
      );
    },
    search() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/tce/maquinas/${this.filter.prefecture_id}/${this.filter.cod}`)
        .then((res) => {
          this.data = res.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
        });
    },
    exportFile() {
      const element = document.createElement("a");
      const content = this.data.map((value, index) => {
        if (index > 0) {
          return `\n${value.export}`;
        }
        return value.export;
      });

      const file = new Blob(content, { type: "text/plain" });
      element.href = URL.createObjectURL(file);
      element.download = `${this.filter.cod}${this.filter.date.substring(
        5
      )}${this.filter.date.substring(0, 4)}Maquinas.txt`;
      document.body.appendChild(element);
      element.click();
    },
  },
};
</script>

<style scoped>
</style>
